import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  forwardRef,
  input,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { LanguageService } from '@core/i18n/language.service';

export interface MultiSelectOption {
  label: string;
  value: any;
  color?: string;
  tagVariant?: 'warning';
  /** Si `true`, la opción se muestra bloqueada: no se puede seleccionar ni deseleccionar. */
  disabled?: boolean;
  /** Raw source item, for consumers that need more than label/value (e.g. address location IDs). */
  data?: any;
}

@Component({
  selector: 'app-multi-select',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './multi-select.component.html',
  styleUrl: './multi-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectComponent),
      multi: true,
    },
  ],
})
export class MultiSelectComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {
  @Input() options: MultiSelectOption[] = [];
  @Input() selectedValues: any[] = [];
  @Input() multiple = true;
  /** Si `true`, cada selección se muestra como un tag con fondo verde transparente. */
  @Input() tagsMode = false;
  @Input() placeholder?: string;
  @Input() searchPlaceholder?: string;
  @Input() noResultsText?: string;
  @Input() disabled = false;
  @Input() selectedLabelFn?: (option: MultiSelectOption) => string;
  searchTermMode = input<boolean>(false);
  @Input() onSearchLocationChange?: (term: string) => void;
  /** Cuando se define, muestra una fila de acción fija al fondo del desplegable
   * (p. ej. "+ Crear nuevo"), visible independientemente del filtro de búsqueda
   * y sin participar en la selección. */
  actionLabel = input<string>('');

  @Output() selectionChange = new EventEmitter<MultiSelectOption[]>();
  @Output() actionClicked = new EventEmitter<void>();

  @ViewChild('searchInput') private searchInput?: ElementRef<HTMLInputElement>;

  searchControl = new FormControl('', { nonNullable: true });
  isOpen = false;
  opensUp = false;
  filteredOptions: MultiSelectOption[] = [];
  /** Index of the keyboard-highlighted option within `filteredOptions`, or -1 for none. */
  activeIndex = -1;
  translateData: Record<string, string> = {};
  private selected = new Set<any>();
  private destroy$ = new Subject<void>();
  private onChange: (value: any[]) => void = () => {};
  private onTouched: () => void = () => {};
  private static nextUid = 0;
  private readonly uid = MultiSelectComponent.nextUid++;

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly cdr: ChangeDetectorRef,
    private readonly langService: LanguageService,
  ) {}

  ngOnInit(): void {
    this.filteredOptions = this.options;
    this.searchControl.valueChanges
      .pipe(debounceTime(200), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((term) => {
        this.applyFilter(term);
        this.onSearchLocationChange?.(term);
      });

    this.langService
      .getSharedDictionary('multi-select')
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.translateData = data;
        this.cdr.markForCheck();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {
      this.applyFilter(this.searchControl.value);
    }
    if (changes['selectedValues']) {
      this.selected = new Set(this.selectedValues ?? []);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get selectedOptions(): MultiSelectOption[] {
    return this.options.filter((o) => this.selected.has(o.value));
  }

  get selectedItems(): string[] {
    const labels = this.selectedOptions.map((o) =>
      this.selectedLabelFn
        ? this.selectedLabelFn(o)
        : o.label
    );

    // Fallback: si el valor del form no está representado en `options`
    // (p. ej. valores cargados de forma asíncrona o con distinto formato),
    // se muestra igualmente para que la UI refleje la selección.
    for (const value of this.selected) {
      if (!this.options.some((o) => o.value === value)) {
        labels.push(String(value));
      }
    }

    return labels;
  }

  /** Cada selección como tag individual (usado cuando `tagsMode` está activo). */
  get selectedChips(): Pick<MultiSelectOption, 'label' | 'value' | 'color' | 'tagVariant'>[] {
    const chips: Pick<MultiSelectOption, 'label' | 'value' | 'color' | 'tagVariant'>[] =
      this.selectedOptions.map((o) => ({
        label: o.label,
        value: o.value,
        color: o.color,
        tagVariant: o.tagVariant,
      }));
    // Fallback: valores seleccionados que no están representados en `options`.
    for (const value of this.selected) {
      if (!this.options.some((o) => o.value === value)) {
        chips.push({ label: String(value), value });
      }
    }
    return chips;
  }

  toggleDropdown(): void {
    if (this.disabled) return;
    this.isOpen ? this.closeDropdown() : this.openDropdown();
  }

  openDropdown(): void {
    if (this.disabled) return;
    this.isOpen = true;
    // Decide whether to open upward when there is not enough room below (sets `opensUp` / `optionsMaxHeight`).
    this.updateDropdownDirection();
    // Highlight the current selection (or the first row) so keyboard use has a starting point.
    this.activeIndex = this.filteredOptions.findIndex((o) => this.selected.has(o.value));
    if (this.activeIndex < 0 && this.filteredOptions.length) this.activeIndex = 0;
    setTimeout(() => this.searchInput?.nativeElement.focus(), 0);
  }

  closeDropdown(): void {
    if (!this.isOpen) return;
    this.onTouched();
    this.isOpen = false;
    // Leaving the dropdown clears the search box: an abandoned term must not linger for the next
    // open, nor get committed as a value. `emitEvent:false` so closing never triggers a fresh
    // (server) search — we just restore the unfiltered list for the next open.
    this.resetSearch();
  }

  private readonly estimatedDropdownHeight = 300;
  private readonly defaultOptionsMaxHeight = 240;
  private readonly minOptionsMaxHeight = 80;

  optionsMaxHeight = this.defaultOptionsMaxHeight;

  private updateDropdownDirection(): void {
    const rect = this.elementRef.nativeElement.getBoundingClientRect();
    const boundary = this.getClippingAncestorRect();
    const viewportTop = boundary ? Math.max(0, boundary.top) : 0;
    const viewportBottom = boundary ? Math.min(window.innerHeight, boundary.bottom) : window.innerHeight;
    const spaceBelow = viewportBottom - rect.bottom;
    const spaceAbove = rect.top - viewportTop;
    this.opensUp = spaceBelow < this.estimatedDropdownHeight && spaceAbove > spaceBelow;

    const availableSpace = this.opensUp ? spaceAbove : spaceBelow;
    const nonOptionsHeight = this.estimatedDropdownHeight - this.defaultOptionsMaxHeight;
    this.optionsMaxHeight = availableSpace < this.estimatedDropdownHeight
      ? Math.max(this.minOptionsMaxHeight, availableSpace - nonOptionsHeight)
      : this.defaultOptionsMaxHeight;
  }

  private getClippingAncestorRect(): DOMRect | null {
    let node = this.elementRef.nativeElement.parentElement;
    while (node && node !== document.body) {
      const { overflow, overflowY } = getComputedStyle(node);
      if (/(auto|scroll|hidden)/.test(overflow) || /(auto|scroll|hidden)/.test(overflowY)) {
        return node.getBoundingClientRect();
      }
      node = node.parentElement;
    }
    return null;
  }

  toggleSelection(option: MultiSelectOption): void {
    if (this.disabled || this.isOptionDisabled(option)) return;
    if (this.multiple) {
      this.selected.has(option.value)
        ? this.selected.delete(option.value)
        : this.selected.add(option.value);
      this.resetSearch();
    } else {
      this.selected = this.selected.has(option.value) ? new Set() : new Set([option.value]);
      this.closeDropdown();
    }
    this.emitSelection();
  }

  writeValue(value: any[] | null): void {
    const hasValue = value != null && (value as unknown) !== '';
    this.selected = new Set(Array.isArray(value) ? value : hasValue ? [value] : []);
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: any[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  isSelected(option: MultiSelectOption): boolean {
    return this.selected.has(option.value);
  }

  isOptionDisabled(option: MultiSelectOption): boolean {
    return !!option.disabled;
  }

  trackByValue(_index: number, option: MultiSelectOption): any {
    return option.value;
  }

  hasResults(): boolean {
    return this.filteredOptions.length > 0;
  }

  get searchTerm(): string {
    return (this.searchControl.value || '').trim();
  }

  addSearchTerm(): void {
    if (this.disabled) return;
    const term = this.searchTerm;
    if (!term) return;
    if (this.multiple) {
      this.selected.add(term);
      this.emitSelection();
      this.resetSearch();
    } else {
      this.selected = new Set([term]);
      this.emitSelection();
      this.closeDropdown();
    }
  }

  onActionClick(): void {
    if (this.disabled) return;
    this.closeDropdown();
    this.actionClicked.emit();
  }

  clearSelection(event: Event): void {
    event.stopPropagation();
    if (this.disabled) return;
    this.selected = new Set();
    this.emitSelection();
  }

  // ---- Keyboard navigation (search box has focus while the dropdown is open) -------------------
  onSearchKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.setActive(this.activeIndex + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.setActive(this.activeIndex - 1);
        break;
      case 'Home':
        event.preventDefault();
        this.setActive(0);
        break;
      case 'End':
        event.preventDefault();
        this.setActive(this.filteredOptions.length - 1);
        break;
      case 'Enter': {
        event.preventDefault();
        const option = this.filteredOptions[this.activeIndex];
        if (option) this.toggleSelection(option);
        else if (this.searchTermMode() && this.searchTerm) this.addSearchTerm();
        break;
      }
      // Escape closes via the document listener below, focused or not.
    }
  }

  setActive(index: number): void {
    const count = this.filteredOptions.length;
    if (!count) {
      this.activeIndex = -1;
      return;
    }
    this.activeIndex = Math.max(0, Math.min(index, count - 1));
    setTimeout(
      () =>
        this.elementRef.nativeElement
          .querySelector('.multi-select__option--active')
          ?.scrollIntoView({ block: 'nearest' }),
      0,
    );
  }

  /** Stable per-instance option id for `aria-activedescendant` (ids must be unique in the page). */
  optionId(index: number): string {
    return `ms-${this.uid}-opt-${index}`;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isOpen && !this.elementRef.nativeElement.contains(event.target as Node)) {
      this.closeDropdown();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen) this.closeDropdown();
  }

  private applyFilter(term: string): void {
    const t = (term || '').trim().toLowerCase();
    this.filteredOptions = t
      ? this.options.filter((o) => o.label.toLowerCase().includes(t))
      : this.options;
    // Keep the highlight in range and on the first match as the list changes while typing.
    this.activeIndex = this.filteredOptions.length ? 0 : -1;
  }

  /** Clears the search box without emitting, and restores the unfiltered option list. */
  private resetSearch(): void {
    this.searchControl.setValue('', { emitEvent: false });
    this.applyFilter('');
  }

  private emitSelection(): void {
    const selectedOptions = this.options.filter((o) => this.selected.has(o.value));
    // Incluye valores seleccionados que no existen en `options`
    // (p. ej. términos añadidos manualmente cuando el buscador no devuelve resultados).
    for (const value of this.selected) {
      if (!this.options.some((o) => o.value === value)) {
        selectedOptions.push({ label: String(value), value });
      }
    }
    this.selectionChange.emit(selectedOptions);
    this.onChange(selectedOptions.map((o) => o.value));
  }
}
